import React, { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { useTheme } from '../../context/ThemeContext'
import '../../styles/admin-pages.css'

// ── Formatters ────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount ?? 0)

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })

// ── SVG icons ─────────────────────────────────────────────────────────────────
function IconDollar({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  )
}
function IconUsers({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}
function IconTrend({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  )
}
function IconTarget({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
  )
}
function IconAlertTriangle({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}
function IconCheck({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}
function IconX({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}
function IconClock({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  )
}
function IconArrowUp({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
    </svg>
  )
}
function IconArrowDown({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" />
    </svg>
  )
}
function IconSettings({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}
function IconDownload({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}
function IconEye({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  )
}
function IconStar({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  )
}

// ── Revenue area chart (pure SVG) ──────────────────────────────────────────────
function RevenueAreaChart({ data = [], height = 240, color = 'var(--accent)' }) {
  if (!data.length) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 12 }}>No data</div>

  const values = data.map(d => typeof d === 'number' ? d : (d.revenue || d.clients || d.overages || 0))
  const max = Math.max(...values, 1)
  const w = 500, h = height - 24

  const pts = values.map((v, i) => {
    const x = values.length > 1 ? (i / (values.length - 1)) * w : w / 2
    const y = h - (v / max) * (h - 12) - 4
    return `${x},${y}`
  })
  const areaPath = `M ${pts.join(' L ')} L ${w},${h} L 0,${h} Z`
  const linePath = `M ${pts.join(' L ')}`

  const xLabels = data.map(d => typeof d === 'object' ? d.name : '').filter(Boolean)

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
        <defs>
          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#revGrad)" />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {values.map((v, i) => {
          const x = values.length > 1 ? (i / (values.length - 1)) * w : w / 2
          const y = h - (v / max) * (h - 12) - 4
          return <circle key={i} cx={x} cy={y} r="3.5" fill={color} stroke="var(--surface)" strokeWidth="2" />
        })}
      </svg>
      {xLabels.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-faint)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>
          {xLabels.filter((_, i) => i % Math.ceil(xLabels.length / 5) === 0 || i === xLabels.length - 1).map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Plan donut chart ───────────────────────────────────────────────────────────
function PlanDonut({ data = [], size = 130, thickness = 16 }) {
  const total = data.reduce((s, d) => s + (d.count || 0), 0)
  if (!total) return null
  const r = (size - thickness) / 2
  const cx = size / 2, cy = size / 2
  const circ = 2 * Math.PI * r
  let offset = 0

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      {data.map((seg, i) => {
        const pct = seg.count / total
        const dash = pct * circ
        const el = (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )
        offset += dash
        return el
      })}
      <text x={cx} y={cy - 5} textAnchor="middle" fontSize="18" fontWeight="700" fill="var(--text)">{total}</text>
      <text x={cx} y={cy + 13} textAnchor="middle" fontSize="10" fill="var(--text-faint)">active</text>
    </svg>
  )
}

// ── Invoice status badge ───────────────────────────────────────────────────────
function InvStatusBadge({ status }) {
  const config = {
    paid:    { icon: <IconCheck />,  label: 'Paid',    cls: 'paid' },
    pending: { icon: <IconClock />,  label: 'Pending', cls: 'pending' },
    failed:  { icon: <IconX />,      label: 'Failed',  cls: 'failed' },
    overdue: { icon: <IconX />,      label: 'Overdue', cls: 'overdue' },
    cancelled: { icon: <IconX />,    label: 'Cancelled', cls: 'failed' },
  }
  const c = config[status] || config.pending
  return (
    <span className={`inv-status ${c.cls}`}>
      {c.icon} {c.label}
    </span>
  )
}

// ── Mini avatar ───────────────────────────────────────────────────────────────
function MiniAvatar({ name = '' }) {
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
  return <span className="mini-avatar">{initials}</span>
}

// ── Main component ─────────────────────────────────────────────────────────────
const BillingDashboard = () => {
  const [selectedMetric, setSelectedMetric] = useState('revenue')
  const [dismissedAlert, setDismissedAlert] = useState(false)
  const queryClient = useQueryClient()

  // ── Queries (preserve all original) ──────────────────────────────────────
  const { data: billingData, isLoading: billingLoading, error: billingError, refetch: refetchBilling } = useQuery(
    'billing-overview',
    () => apiService.get('/billing/overview').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  const { data: overagesData, isLoading: overagesLoading, error: overagesError } = useQuery(
    'billing-overages',
    () => apiService.get('/billing/overages').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  const { data: plansData, error: plansError } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data),
    { retry: 1, staleTime: 10 * 60 * 1000 }
  )

  const { data: historicalData, error: historicalError } = useQuery(
    'billing-historical',
    () => apiService.get('/billing/historical').then(res => res.data),
    { retry: 1, staleTime: 10 * 60 * 1000 }
  )

  const { data: growthData, error: growthError } = useQuery(
    'billing-growth',
    () => apiService.get('/billing/growth').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  // ── Mutations (preserve all original) ─────────────────────────────────────
  const updateSummaryMutation = useMutation(
    () => apiService.post('/billing/update-summary'),
    {
      onSuccess: () => {
        toast.success('Billing summary updated successfully')
        queryClient.invalidateQueries('billing-overview')
        queryClient.invalidateQueries('billing-overages')
        queryClient.invalidateQueries('billing-historical')
        queryClient.invalidateQueries('billing-growth')
      },
      onError: () => toast.error('Failed to update billing summary')
    }
  )

  const hasErrors = billingError || overagesError || plansError || historicalError || growthError
  const isLoading = billingLoading || overagesLoading

  const chartData = historicalData || [
    { name: 'Jan', revenue: 0, clients: 0, overages: 0 },
    { name: 'Feb', revenue: 0, clients: 0, overages: 0 },
    { name: 'Mar', revenue: 0, clients: 0, overages: 0 },
    { name: 'Apr', revenue: 0, clients: 0, overages: 0 },
    { name: 'May', revenue: 0, clients: 0, overages: 0 },
    { name: 'Jun', revenue: 0, clients: 0, overages: 0 },
  ]

  const growthMetrics = growthData || {
    totalRevenue:         { current: 0, previous: 0, growth: 0 },
    baseRevenue:          { current: 0, previous: 0, growth: 0 },
    overageRevenue:       { current: 0, previous: 0, growth: 0 },
    activeSubscriptions:  { current: 0, previous: 0, growth: 0 },
    avgRevenuePerClient:  { current: 0, previous: 0, growth: 0 },
    churnRate:            { current: 2.5, previous: 2.5, growth: 0 },
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
        <LoadingSpinner size="lg" text="Loading billing data..." />
      </div>
    )
  }

  const displayBillingData = billingData || {
    totalRevenue: 0, baseRevenue: 0, overageRevenue: 0, activeSubscriptions: 0,
    planBreakdown: [], clientBilling: [], lastUpdated: new Date().toISOString()
  }
  const displayOveragesData = overagesData || { totalOverageRevenue: 0, overageCount: 0, overages: [] }

  const totalRevenue      = displayBillingData.totalRevenue || 0
  const overageRevenue    = displayBillingData.overageRevenue || 0
  const activeSubscriptions = displayBillingData.activeSubscriptions || 0
  const planBreakdown     = displayBillingData.planBreakdown || []
  const clientBilling     = displayBillingData.clientBilling || []

  const failedPayments = clientBilling.filter(c => c.status === 'failed' || c.status === 'overdue')
  const PLAN_COLORS = { basic: '#22c55e', pro: '#6c63ff', enterprise: '#f59e0b' }

  const planDonutData = planBreakdown.map(p => ({
    name: plansData?.[p.plan_id]?.name || p.plan_id,
    count: p.count || 0,
    price: plansData?.[p.plan_id]?.price || 0,
    color: PLAN_COLORS[p.plan_id] || '#a3a3a3',
    plan_id: p.plan_id,
  }))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 4 }}>

      {/* ── Header ─── */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Billing</div>
          <h1>Billing</h1>
          <div className="subtitle">Revenue, invoices and subscription health</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="ds-btn ds-btn-ghost"
            onClick={() => updateSummaryMutation.mutate()}
            disabled={updateSummaryMutation.isLoading}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {updateSummaryMutation.isLoading ? <LoadingSpinner size="sm" /> : <><IconSettings size={14} /> Update</>}
          </button>
        </div>
      </div>

      {/* ── Failed payments alert ─── */}
      {failedPayments.length > 0 && !dismissedAlert && (
        <div className="failed-alert">
          <div className="failed-alert-icon">
            <span style={{ color: '#ea580c' }}><IconAlertTriangle size={18} /></span>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 3px', fontSize: 13.5, fontWeight: 600, color: '#7c2d12' }}>
              {failedPayments.length} failed payment{failedPayments.length > 1 ? 's' : ''} need attention
            </p>
            <p style={{ margin: 0, fontSize: 12, color: '#9a3412' }}>
              {failedPayments.map(c => c.companyName).join(', ')}
            </p>
          </div>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ea580c', padding: 4 }}
            onClick={() => setDismissedAlert(true)}
            title="Dismiss"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Error banner ─── */}
      {hasErrors && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#b45309' }}><IconAlertTriangle size={18} /></span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#78350f' }}>Unable to load live billing data</p>
            <p style={{ margin: 0, fontSize: 11.5, color: '#92400e' }}>Showing partial or cached data.</p>
          </div>
          <button
            className="tbl-action-btn"
            onClick={() => {
              refetchBilling()
              queryClient.invalidateQueries('billing-overages')
              queryClient.invalidateQueries('billing-plans')
              queryClient.invalidateQueries('billing-historical')
              queryClient.invalidateQueries('billing-growth')
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Stat row ─── */}
      <div className="billing-stats">
        {[
          {
            label: 'MRR',
            value: formatCurrency(totalRevenue),
            sub: `${growthMetrics.totalRevenue.growth >= 0 ? '+' : ''}${growthMetrics.totalRevenue.growth}% vs last month`,
            delta: growthMetrics.totalRevenue.growth >= 0 ? 'up' : 'down',
            icon: <IconDollar />,
            iconBg: '#e0e7ff', iconColor: '#4338ca',
          },
          {
            label: 'Active Subscriptions',
            value: activeSubscriptions.toLocaleString(),
            sub: `${growthMetrics.activeSubscriptions.growth >= 0 ? '+' : ''}${growthMetrics.activeSubscriptions.growth}% vs last month`,
            delta: growthMetrics.activeSubscriptions.growth >= 0 ? 'up' : 'down',
            icon: <IconUsers />,
            iconBg: '#dcfce7', iconColor: '#15803d',
          },
          {
            label: 'Overage Revenue',
            value: formatCurrency(overageRevenue),
            sub: `${growthMetrics.overageRevenue.growth >= 0 ? '+' : ''}${growthMetrics.overageRevenue.growth}% vs last month`,
            delta: growthMetrics.overageRevenue.growth >= 0 ? 'up' : 'down',
            icon: <IconTrend />,
            iconBg: '#fef3c7', iconColor: '#b45309',
          },
          {
            label: 'Avg / Client',
            value: formatCurrency(growthMetrics.avgRevenuePerClient.current),
            sub: `${growthMetrics.avgRevenuePerClient.growth >= 0 ? '+' : ''}${growthMetrics.avgRevenuePerClient.growth}% vs last month`,
            delta: growthMetrics.avgRevenuePerClient.growth >= 0 ? 'up' : 'down',
            icon: <IconTarget />,
            iconBg: '#fce7f3', iconColor: '#be185d',
          },
        ].map((s, i) => (
          <div key={i} className="billing-stat-card">
            <div className="billing-stat-label">
              {s.label}
              <div style={{ width: 32, height: 32, borderRadius: 9, background: s.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: s.iconColor }}>{s.icon}</span>
              </div>
            </div>
            <div className="billing-stat-value">{s.value}</div>
            <div className="billing-stat-sub" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span className={`delta-badge ${s.delta}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                {s.delta === 'up' ? <IconArrowUp /> : <IconArrowDown />}
              </span>
              {s.sub}
            </div>
          </div>
        ))}
      </div>

      {/* ── Chart row ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>

        {/* Revenue chart */}
        <div className="revenue-chart-card">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h3>Revenue trend</h3>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>Rolling monthly, GBP</p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div className="tabs">
                <button>30d</button>
                <button className="active">90d</button>
                <button>YTD</button>
              </div>
              <select
                value={selectedMetric}
                onChange={e => setSelectedMetric(e.target.value)}
                style={{ fontSize: 12, border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px', background: 'var(--surface)', color: 'var(--text)', outline: 'none', cursor: 'pointer' }}
              >
                <option value="revenue">Revenue</option>
                <option value="clients">Clients</option>
                <option value="overages">Overages</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>
              {formatCurrency(chartData.reduce((s, d) => s + (d[selectedMetric] || 0), 0))}
            </div>
            <span className="delta-badge up" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <IconArrowUp /> +18.2%
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>vs prev period</span>
          </div>
          <RevenueAreaChart data={chartData.map(d => ({ ...d, name: d.name }))} height={220} color="var(--accent)" />
        </div>

        {/* Plan distribution */}
        <div className="plan-donut-card">
          <h3 style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Plan distribution</h3>
          <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text-muted)' }}>By active subscribers</p>

          {planDonutData.length > 0 ? (
            <>
              <div className="donut-wrap" style={{ marginBottom: 16 }}>
                <PlanDonut data={planDonutData} size={120} thickness={14} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {planDonutData.map((p, i) => (
                    <div key={i} className="plan-legend-row">
                      <span className="plan-legend-dot" style={{ background: p.color }} />
                      <span style={{ color: 'var(--text-muted)', flex: 1, fontSize: 12 }}>{p.name}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                {planDonutData.map((p, i) => (
                  <div key={i} style={{ fontSize: 11.5, color: 'var(--text-faint)', marginBottom: 3 }}>
                    {formatCurrency(p.price)} × {p.count} = <strong style={{ color: 'var(--text-muted)' }}>{formatCurrency(p.price * p.count)}/mo</strong>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0', color: 'var(--text-faint)', gap: 8 }}>
              <IconStar size={36} />
              <p style={{ margin: 0, fontSize: 13 }}>No active subscriptions</p>
            </div>
          )}

          <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 12 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-faint)', marginBottom: 6 }}>Churn &amp; forecast</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text)' }}>{growthMetrics.churnRate.current}%</strong> churn this month.
            </div>
          </div>
        </div>
      </div>

      {/* ── Invoices table ─── */}
      <div className="invoices-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 14px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Client billing</h3>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{clientBilling.length} clients · {failedPayments.length} need attention</p>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="invoices-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Plan Cost</th>
                <th>Overages</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clientBilling.length > 0 ? clientBilling.map((client, i) => (
                <tr key={client.clientId || i}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <MiniAvatar name={client.companyName || ''} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{client.companyName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>ID: {String(client.clientId || '').slice(0, 8)}&hellip;</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block', fontSize: 11.5, fontWeight: 600,
                      padding: '3px 9px', borderRadius: 999,
                      background: client.plan === 'basic' ? '#dcfce7' : client.plan === 'pro' ? 'rgba(108,99,255,0.12)' : '#fef3c7',
                      color: client.plan === 'basic' ? '#15803d' : client.plan === 'pro' ? 'var(--accent)' : '#b45309',
                    }}>
                      {client.planName || plansData?.[client.plan]?.name || client.plan}
                    </span>
                  </td>
                  <td><InvStatusBadge status={client.status || 'paid'} /></td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{formatCurrency(client.planCost)}</td>
                  <td>
                    {client.overage?.hasOverage ? (
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#b45309' }}>+{client.overage.overage} edits</div>
                        <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{formatCurrency(client.overage.overageCost)}</div>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>None</span>
                    )}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14 }}>{formatCurrency(client.totalCost)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="tbl-action-btn" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IconEye size={12} /> View</button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-faint)', fontSize: 13 }}>
                    No billing data available for this period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Usage overages ─── */}
      {displayOveragesData.overages?.length > 0 && (
        <div className="analytics-chart-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Usage overages</h3>
              <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Clients over their plan limits this month</p>
            </div>
            {displayOveragesData.overageCount > 0 && (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309', background: '#fef3c7', borderRadius: 999, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <IconAlertTriangle size={13} /> {displayOveragesData.overageCount} clients
              </span>
            )}
          </div>
          {overagesLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {displayOveragesData.overages.slice(0, 5).map((o, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, background: '#fed7aa', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#ea580c' }}><IconAlertTriangle size={16} /></span>
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{o.companyName}</div>
                      <div style={{ fontSize: 11.5, color: '#9a3412' }}>{o.used} / {o.quota} edits (+{o.overage} over)</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#b45309', fontFamily: 'var(--font-mono)' }}>{formatCurrency(o.overageCost)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>overage fee</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Last updated ─── */}
      {displayBillingData.lastUpdated && (
        <div style={{ textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-faint)', background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 999, padding: '5px 14px' }}>
            <IconClock size={13} /> Last updated: {formatDate(displayBillingData.lastUpdated)}
          </span>
        </div>
      )}

    </div>
  )
}

export default BillingDashboard
